import { periodsOverlap } from "@swivl-mono/helpers";
import { WeeklyPeriod } from "@swivl-mono/types";
import { Button, ButtonVariant, ErrorText } from "@swivl-mono/ui";
import { useState } from "react";
import { TbX } from "react-icons/tb";

import { CLOSED, OVERLAP_ERROR, formatPeriods, hhmmToMinutes, minutesToHHMM, nextWindowAfter } from "./scheduleTime";

export type ClosureType = "closed" | "reduced";

export interface DraftPeriod {
  openMinute: number;
  closeMinute: number;
}

const DEFAULT_REDUCED_PERIOD: DraftPeriod = { openMinute: 9 * 60, closeMinute: 13 * 60 };

const TIME_INPUT_CLASS =
  "body-sm rounded-sm border border-grey-400 px-2 py-1 text-grey-900 hover:border-orange-500 focus:border-orange-500 outline-none";

export interface ClosureDraft {
  type: ClosureType;
  setType: (next: ClosureType) => void;
  periods: DraftPeriod[];
  updatePeriod: (idx: number, patch: Partial<DraftPeriod>) => void;
  addPeriod: () => void;
  removePeriod: (idx: number) => void;
  reset: () => void;
  isValid: boolean;
  /** Serialize for the API: null when closed all day. */
  toPeriods: () => WeeklyPeriod[] | null;
}

/**
 * Shared "closed all day vs open with reduced hours" draft, used by both the
 * date-override and holiday-rule editors — the two carry the same shape and
 * previously kept identical copies of this state.
 *
 * Reduced-hours periods are stored with a placeholder `dayOfWeek`: the date (or
 * the holiday's RRULE) already pins the day down, and neither resolver reads
 * `dayOfWeek` for these rows.
 */
export function useClosureDraft(): ClosureDraft {
  const [type, setType] = useState<ClosureType>("closed");
  const [periods, setPeriods] = useState<DraftPeriod[]>([DEFAULT_REDUCED_PERIOD]);

  return {
    type,
    setType,
    periods,
    updatePeriod: (idx, patch) => setPeriods((prev) => prev.map((p, i) => (i === idx ? { ...p, ...patch } : p))),
    addPeriod: () => setPeriods((prev) => [...prev, nextWindowAfter(prev, DEFAULT_REDUCED_PERIOD)]),
    removePeriod: (idx) => setPeriods((prev) => prev.filter((_, i) => i !== idx)),
    reset: () => {
      setType("closed");
      setPeriods([DEFAULT_REDUCED_PERIOD]);
    },
    isValid: type === "closed" || (periods.length > 0 && !periodsOverlap(periods)),
    toPeriods: () => (type === "reduced" ? periods.map((p) => ({ dayOfWeek: 0, openMinute: p.openMinute, closeMinute: p.closeMinute })) : null)
  };
}

/** Read-only description of a closure, shared by both lists. */
export function closureSummary(isClosed: boolean, periods: { openMinute: number; closeMinute: number }[] | null | undefined): string {
  if (isClosed) return `${CLOSED} all day`;
  if (periods && periods.length > 0) return `Reduced hours: ${formatPeriods(periods)}`;
  return "Open (no closure)";
}

interface ClosureTypeChoiceProps {
  /** Radio group name — must be unique per form on the page. */
  name: string;
  label: string;
  closure: ClosureDraft;
}

export function ClosureTypeChoice({ name, label, closure }: ClosureTypeChoiceProps): React.ReactElement {
  return (
    <div>
      <p className="body-sm-alt text-grey-600">{label}</p>
      <div className="mt-1 flex gap-4">
        <label className="body-sm inline-flex cursor-pointer items-center gap-2 text-grey-900">
          <input
            type="radio"
            name={name}
            className="accent-orange-500"
            checked={closure.type === "closed"}
            onChange={() => closure.setType("closed")}
          />
          Closed all day
        </label>
        <label className="body-sm inline-flex cursor-pointer items-center gap-2 text-grey-900">
          <input
            type="radio"
            name={name}
            className="accent-orange-500"
            checked={closure.type === "reduced"}
            onChange={() => closure.setType("reduced")}
          />
          Open with reduced hours
        </label>
      </div>
    </div>
  );
}

/** The open-window rows, shown only when "reduced hours" is selected. */
export function ClosurePeriodFields({ closure }: { closure: ClosureDraft }): React.ReactElement | null {
  if (closure.type !== "reduced") return null;
  return (
    <div className="space-y-2 rounded-sm border border-grey-200 p-3">
      {closure.periods.map((p, idx) => (
        <div key={idx} className="flex items-center gap-2">
          <input
            type="time"
            aria-label="Opening time"
            value={minutesToHHMM(p.openMinute)}
            onChange={(e) => closure.updatePeriod(idx, { openMinute: hhmmToMinutes(e.target.value) })}
            className={TIME_INPUT_CLASS}
          />
          <span className="body-sm text-grey-500">–</span>
          <input
            type="time"
            aria-label="Closing time"
            value={minutesToHHMM(p.closeMinute)}
            onChange={(e) => closure.updatePeriod(idx, { closeMinute: hhmmToMinutes(e.target.value) })}
            className={TIME_INPUT_CLASS}
          />
          {closure.periods.length > 1 && (
            <button
              type="button"
              onClick={() => closure.removePeriod(idx)}
              className="cursor-pointer text-grey-400 hover:text-red-500"
              title="Remove this window"
              aria-label="Remove window"
            >
              <TbX className="size-4" />
            </button>
          )}
        </div>
      ))}
      {periodsOverlap(closure.periods) && <ErrorText>{OVERLAP_ERROR}</ErrorText>}
      <Button variant={ButtonVariant.TEXT} onClick={closure.addPeriod}>
        Add another window
      </Button>
    </div>
  );
}
